import React, { useEffect } from 'react';
import { AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, ExternalLinkIcon, LandmarkIcon, MapPinIcon, UsersIcon, UserIcon, XIcon } from 'lucide-react';
import { AlertType, Loan } from '../data/mockData';
interface LoanRecordPreviewProps {
  loan: Loan;
  onClose: () => void;
}
export function LoanRecordPreview({
  loan,
  onClose
}: LoanRecordPreviewProps) {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(loan.amount);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1856]/55 p-4 sm:p-6" role="presentation" onMouseDown={onClose} data-id="element-70" data-mp="ih0mjg7vumj2">
      <section role="dialog" aria-modal="true" aria-labelledby="loan-record-title" className="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto border border-[#DCE2EC] bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:max-h-[calc(100vh-3rem)]" onMouseDown={event => event.stopPropagation()} data-id="element-71" data-mp="3zv74j9rmu94">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b-4 border-[#FF8000] bg-white px-5 py-5 dark:bg-gray-800 sm:px-7" data-id="element-72" data-mp="3h7ercl8j3v8m">
          <div data-id="element-73" data-mp="3m7auyjdbdknn">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#009EDB]" data-id="element-74" data-mp="3m6rp410xumpz">
              Loan record preview
            </p>
            <h1 id="loan-record-title" className="mt-1 text-xl font-bold tracking-[-0.04em] text-[#0B1856] dark:text-white sm:text-2xl" data-id="element-75" data-mp="3v6y09g95zalz">
              {loan.borrowerName}
            </h1>
            <p className="mt-1 font-mono text-xs text-[#0B1856]/65 dark:text-gray-300" data-id="element-76" data-mp="1afxif5mual38">
              Loan #{loan.loanNumber}
            </p>
          </div>
          <button type="button" onClick={onClose} className="ml-4 flex h-9 w-9 flex-shrink-0 items-center justify-center text-[#0B1856] transition-colors hover:bg-[#F4F9FD] hover:text-[#009EDB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009EDB] dark:text-white dark:hover:bg-gray-700" aria-label="Close loan record preview" data-id="element-77" data-mp="32iicbglgrvql">
            <XIcon className="h-5 w-5" data-id="element-78" data-mp="2js0nvo7nzmb1" />
          </button>
        </div>

        <div className="grid gap-0 lg:grid-cols-[1.55fr_0.9fr]" data-id="element-79" data-mp="2jpxle4pbs0pf">
          <div className="p-5 sm:p-7" data-id="element-80" data-mp="2xkgpqlotxead">
            <div className="flex flex-col gap-4 border-b border-[#E4E8F0] pb-5 dark:border-gray-700 sm:flex-row sm:items-start sm:justify-between" data-id="element-81" data-mp="2xli16r9rc2sm">
              <div data-id="element-82" data-mp="252cmkmzhgnnb">
                <p className="text-sm text-[#0B1856]/65 dark:text-gray-300" data-id="element-83" data-mp="251jkhzbgymbi">
                  {loan.propertyAddress}
                </p>
                <div className="mt-4 inline-block border-l-4 border-[#009EDB] bg-[#F4F9FD] px-4 py-2 dark:bg-[#009EDB]/10" data-id="element-84" data-mp="fm0ie3x04o7i">
                  <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#0B1856]/60 dark:text-gray-300" data-id="element-85" data-mp="kd8uj7ppunod">
                    Loan amount
                  </p>
                  <p className="mt-0.5 text-xl font-bold text-[#0B1856] dark:text-white" data-id="element-86" data-mp="bkexbvabd3m8">
                    {formattedAmount}
                  </p>
                </div>
              </div>
              <button type="button" className="inline-flex items-center justify-center gap-2 bg-[#FF8000] px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#e67300] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009EDB] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800" aria-label="View Salesforce Record for this loan" data-id="element-87" data-mp="z858m1k7i59d">
                View Salesforce Record
                <ExternalLinkIcon className="h-4 w-4" data-id="element-88" data-mp="3eyrguppa7crz" />
              </button>
            </div>

            <h2 className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B1856] dark:text-white" data-id="element-89" data-mp="3uclq4wq1ze8u">
              Loan overview
            </h2>
            <dl className="mt-4 grid gap-x-8 gap-y-5 sm:grid-cols-2" data-id="element-90" data-mp="3lj8nv463n0a3">
              <DetailItem icon={<LandmarkIcon data-id="element-92" data-mp="3gkfadbqtb99x" />} label="Current stage" value={loan.stage} emphasis data-id="element-91" data-mp="3gjw1ekj53v81" />
              <DetailItem icon={<ClockIcon data-id="element-94" data-mp="h37k7e872h47" />} label="Time in stage" value={`${loan.daysInStage} ${loan.daysInStage === 1 ? 'day' : 'days'}`} data-id="element-93" data-mp="h4qxcvyk0c1r" />
              <DetailItem icon={<MapPinIcon data-id="element-96" data-mp="d0t06qsshbbc" />} label="Property address" value={loan.propertyAddress} data-id="element-95" data-mp="d3f66m017irg" />
              <DetailItem icon={<UserIcon data-id="element-98" data-mp="85dpu8tzpxjo" />} label="Loan officer" value={loan.loanOfficer} data-id="element-97" data-mp="81px3bwddf60" />
              <DetailItem icon={<UsersIcon data-id="element-100" data-mp="36u160o0i7nm" />} label="Team lead" value={loan.teamLead} data-id="element-99" data-mp="347v65grs07i" />
              <DetailItem icon={<UsersIcon data-id="element-102" data-mp="2sroyt3mtms06" />} label="Division lead" value={loan.divisionLead} data-id="element-101" data-mp="2svcuo9fqnqi2" />
            </dl>
          </div>

          <aside className="border-t border-[#E4E8F0] bg-[#F8FAFD] p-5 dark:border-gray-700 dark:bg-gray-800 lg:border-l lg:border-t-0 sm:p-7" data-id="element-103" data-mp="2nx1ktypg1fzu">
            <div className="flex items-center justify-between" data-id="element-104" data-mp="2ip1qdrrs3z5f">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B1856] dark:text-white" data-id="element-105" data-mp="2noxzu6j8wisd">
                Attention items
              </h2>
              <span className="bg-[#0B1856] px-2 py-1 text-[10px] font-bold text-white" data-id="element-106" data-mp="2sh7m9cl5j0pw">
                {loan.alerts.length}
              </span>
            </div>
            {loan.alerts.length > 0 ? <div className="mt-4 space-y-2.5" data-id="element-107" data-mp="31ehx4wu24nua">
                {loan.alerts.map(alert => <div key={alert} className={`flex items-start gap-2 border px-3 py-3 text-sm font-semibold ${getAlertStyle(alert)}`} data-id="element-108" data-mp="36g7686xpyrfq">
                    {getAlertIcon(alert)}
                    <span data-id="element-109" data-mp="1h07r897f67s6">{alert}</span>
                  </div>)}
              </div> : <div className="mt-4 border border-dashed border-[#C9D6E6] px-4 py-5 text-center text-sm text-[#0B1856]/65 dark:border-gray-600 dark:text-gray-300" data-id="element-110" data-mp="18z064pphudi1">
                No active attention items.
              </div>}
          </aside>
        </div>
      </section>
    </div>;
}
interface DetailItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  emphasis?: boolean;
}
function DetailItem({
  icon,
  label,
  value,
  emphasis = false
}: DetailItemProps) {
  return <div className="flex items-start gap-3" data-id="element-111" data-mp="1n0cjquh6m30r">
      <span className="mt-0.5 text-[#009EDB] [&>svg]:h-4 [&>svg]:w-4" aria-hidden="true" data-id="element-112" data-mp="1mxi198hqas9z">
        {icon}
      </span>
      <div data-id="element-113" data-mp="1wpdzqknq8keq">
        <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#0B1856]/55 dark:text-gray-400" data-id="element-114" data-mp="1wpnsuh42j7on">
          {label}
        </dt>
        <dd className={`mt-1 text-sm ${emphasis ? 'font-bold text-[#0B1856] dark:text-white' : 'font-semibold text-gray-700 dark:text-gray-200'}`} data-id="element-115" data-mp="2apyg7a666452">
          {value}
        </dd>
      </div>
    </div>;
}
function getAlertStyle(alert: AlertType) {
  switch (alert) {
    case 'Rate Lock Expiring':
    case 'Missing Documents':
    case 'Closing Date Approaching':
      return 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200';
    case 'AWC No Appraisal Back':
    case 'Disclosures Out Not Signed':
    case 'Disclosures Signed Not Submitted':
      return 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-200';
    case 'AWC Appraisal Back':
      return 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200';
  }
}
function getAlertIcon(alert: AlertType) {
  const className = 'mt-0.5 h-4 w-4 flex-shrink-0';
  switch (alert) {
    case 'Rate Lock Expiring':
    case 'Missing Documents':
    case 'Closing Date Approaching':
      return <AlertCircleIcon className={className} data-id="element-116" data-mp="1sl209yguz21k" />;
    case 'AWC No Appraisal Back':
    case 'Disclosures Out Not Signed':
    case 'Disclosures Signed Not Submitted':
      return <AlertTriangleIcon className={className} data-id="element-117" data-mp="2349zc5nabgzx" />;
    case 'AWC Appraisal Back':
      return <CheckCircleIcon className={className} data-id="element-118" data-mp="1z2ex8yxj4zl3" />;
  }
}